import type { AppManifest } from '@yada/types';
import React from 'react';

/**
 * Centralized registry of all first-party YADA apps.
 * Uses dynamic imports to keep the initial shell bundle small.
 */

export const habitsManifest: AppManifest = {
  id: '@yada/app-habits',
  name: 'Habits',
  version: '0.0.0',
  slots: { main: null, leftDrawer: null, header: null, settings: null },
  load: () => import('@yada/app-habits').then(m => {
    habitsManifest.slots = m.manifest.slots;
    return m.pluginModule;
  }),
  metadataExtractor: (data: any) => ({
    documentType: 'habit',
    itemCount: (data.habits as any[])?.length || 0,
  }),
};

export const mealPlannerManifest: AppManifest = {
  id: '@yada/app-meal-planner',
  name: 'Meal Planner',
  version: '0.0.0',
  slots: { main: null, leftDrawer: null, header: null, settings: null },
  load: () => import('@yada/app-meal-planner').then(m => {
    mealPlannerManifest.slots = m.manifest.slots;
    return m.pluginModule;
  }),
  permissions: {
    crossAppStorage: ['@yada/app-calendar'],
  },
  metadataExtractor: (data: any) => ({
    documentType: 'meal',
    itemCount: (data?.recipes?.length || 0) + (data?.plan?.meals?.length || 0),
  }),
};

export const homeManifest: AppManifest = {
  id: '@yada/app-home',
  name: 'Home',
  version: '0.0.0',
  slots: { main: null, leftDrawer: null, header: null, settings: null },
  load: () => import('@yada/app-home').then(m => {
    homeManifest.slots = m.manifest.slots;
    return m.pluginModule;
  }),
  permissions: {
    crossAppStorage: [
      '@yada/app-calendar',
      '@yada/app-chat',
      '@yada/app-contacts',
      '@yada/app-docs',
      '@yada/app-finance',
      '@yada/app-habits',
      '@yada/app-meal-planner',
      '@yada/app-shopping-list',
      '@yada/app-todo',
      '@yada/app-training'
    ],
  },
};

export const docsManifest: AppManifest = {
  id: '@yada/app-docs',
  name: 'Docs',
  version: '0.0.0',
  slots: { main: null, leftDrawer: null, header: null, settings: null },
  load: () => import('@yada/app-docs').then(m => {
    docsManifest.slots = m.manifest.slots;
    return m.pluginModule;
  }),
  openDrawerByDefault: true,
  metadataExtractor: (data: any) => ({
    documentType: 'doc',
    itemCount: (data.docs as any[])?.length || 0,
  }),
};

export const calendarManifest: AppManifest = {
  id: '@yada/app-calendar',
  name: 'Calendar',
  version: '0.0.0',
  slots: { main: null, leftDrawer: null, header: null, settings: null },
  load: () => import('@yada/app-calendar').then(m => {
    calendarManifest.slots = m.manifest.slots;
    return m.pluginModule;
  }),
  permissions: {
    crossAppStorage: [],
  },
  widgets: [
    {
      id: '@yada/calendar-pomodoro',
      slot: 'navbar',
      // Lazy load the widget component too
      component: React.lazy(() => import('@yada/app-calendar').then(m => ({ default: m.PomodoroWidget }))),
      order: 10,
    },
  ],
  metadataExtractor: (data: any) => ({
    documentType: 'event',
    itemCount: data?.events?.length || 0,
  }),
};

export const communityVotesManifest: AppManifest = {
  id: '@yada/app-community-votes',
  name: 'Community Votes',
  version: '0.0.0',
  slots: { main: null, leftDrawer: null, header: null, settings: null },
  load: () => import('@yada/app-community-votes').then(m => {
    communityVotesManifest.slots = m.manifest.slots;
    return m.pluginModule;
  }),
};

export const chatManifest: AppManifest = {
  id: '@yada/app-chat',
  name: 'Chat',
  version: '0.0.0',
  slots: { main: null, leftDrawer: null, header: null, settings: null },
  load: () => import('@yada/app-chat').then(m => {
    chatManifest.slots = m.manifest.slots;
    return m.pluginModule;
  }),
  openDrawerByDefault: true,
  permissions: {
    crossAppStorage: [],
  },
  metadataExtractor: (data: any) => {
    const convs = data?.conversations || {};
    return {
      documentType: 'conversation',
      itemCount: Object.keys(convs).length,
    };
  },
};

export const trainingManifest: AppManifest = {
  id: '@yada/app-training',
  name: 'Training',
  version: '0.0.0',
  slots: { main: null, leftDrawer: null, header: null, settings: null },
  load: () => import('@yada/app-training').then(m => {
    trainingManifest.slots = m.manifest.slots;
    return m.pluginModule;
  }),
  metadataExtractor: (data: any) => ({
    documentType: 'workout',
    itemCount: data?.workouts?.length || 0,
  }),
};

export const contactsManifest: AppManifest = {
  id: '@yada/app-contacts',
  name: 'Contacts',
  version: '0.1.0',
  slots: { main: null, leftDrawer: null, header: null, settings: null },
  load: () => import('@yada/app-contacts').then(m => {
    contactsManifest.slots = m.manifest.slots;
    return m.pluginModule;
  }),
  permissions: {
    crossAppStorage: [
      '@yada/app-home',
      '@yada/app-finance',
      '@yada/app-calendar',
      '@yada/app-chat'
    ],
  },
  openDrawerByDefault: true,
};

export const financeManifest: AppManifest = {
  id: '@yada/app-finance',
  name: 'Finance',
  version: '0.1.0',
  slots: { main: null, leftDrawer: null, header: null, settings: null },
  load: () => import('@yada/app-finance').then(m => {
    financeManifest.slots = m.manifest.slots;
    return m.pluginModule;
  }),
  permissions: {
    crossAppStorage: [],
  },
  metadataExtractor: (data: any) => ({
    documentType: 'financial-timeline',
    itemCount: (data?.financial_events?.length ?? data?.invoices?.length) || 0,
  }),
};

export const shoppingListManifest: AppManifest = {
  id: '@yada/app-shopping-list',
  name: 'Shopping List',
  version: '0.0.0',
  slots: { main: null, leftDrawer: null, header: null, settings: null },
  load: () => import('@yada/app-shopping-list').then(m => {
    shoppingListManifest.slots = m.manifest.slots;
    return m.pluginModule;
  }),
  openDrawerByDefault: true,
  metadataExtractor: (data: any) => ({
    documentType: 'shopping-list',
    itemCount: data?.lists?.length || 0,
  }),
};

export const todoManifest: AppManifest = {
  id: '@yada/app-todo',
  name: 'Todo',
  version: '0.0.0',
  slots: { main: null, leftDrawer: null, header: null, settings: null },
  load: () => import('@yada/app-todo').then(m => {
    todoManifest.slots = m.manifest.slots;
    return m.pluginModule;
  }),
  openDrawerByDefault: true,
  metadataExtractor: (data: any) => ({
    documentType: 'todo',
    itemCount: data?.todos?.length || 0,
  }),
};

export const groupsManifest: AppManifest = {
  id: '@yada/app-groups',
  name: 'Groups',
  version: '0.1.0',
  slots: { main: null, leftDrawer: null, header: null, settings: null },
  load: () => import('@yada/app-groups').then(m => {
    groupsManifest.slots = m.manifest.slots;
    return m.pluginModule;
  }),
  permissions: {
    crossAppStorage: ['@yada/app-chat'],
  },
  openDrawerByDefault: true,
  metadataExtractor: (data: any) => ({
    documentType: 'group',
    itemCount: data?.groups?.groups?.length || 0,
  }),
};

export const contentDistributionManifest: AppManifest = {
  id: '@yada/app-content-distribution',
  name: 'Publish',
  version: '0.2.0',
  slots: { main: null, leftDrawer: null, header: null, settings: null },
  load: () => import('@yada/app-content-distribution').then(m => {
    contentDistributionManifest.slots = m.manifest.slots;
    return m.pluginModule;
  }),
};

export const devtoolsManifest: AppManifest = {
  id: '@yada/app-devtools',
  name: 'DevTools',
  version: '0.0.0',
  slots: { main: null, leftDrawer: null, header: null, settings: null },
  load: () => import('@yada/app-devtools').then(m => {
    devtoolsManifest.slots = m.manifest.slots;
    return m.pluginModule;
  }),
  developerOnly: true,
};

export const emojiManifest: AppManifest = {
  id: '@yada/app-emoji',
  name: 'Emoji',
  version: '0.0.0',
  slots: { main: null, leftDrawer: null, header: null, settings: null },
  load: () => import('@yada/app-emoji').then(m => {
    emojiManifest.slots = m.manifest.slots;
    return m.pluginModule;
  }),
};

export const adminManifest: AppManifest = {
  id: '@yada/app-admin',
  name: 'Admin',
  version: '0.0.0',
  slots: { main: null, leftDrawer: null, header: null, settings: null },
  load: () => import('@yada/app-admin').then(m => {
    adminManifest.slots = m.manifest.slots;
    return m.pluginModule;
  }),
};

export const eventsManifest: AppManifest = {
  id: '@yada/app-events',
  name: 'Events',
  version: '0.1.0',
  slots: { main: null, leftDrawer: null, header: null, settings: null },
  openDrawerByDefault: true,
  load: () => import('@yada/app-events').then(m => {
    eventsManifest.slots = m.manifest.slots;
    return m.pluginModule;
  }),
  permissions: {
    crossAppStorage: ['@yada/app-calendar'],
  },
  metadataExtractor: (data: any) => ({
    documentType: 'event',
    itemCount: Array.isArray(data?.events) ? data.events.length : 0,
  }),
};

export const tripsManifest: AppManifest = {
  id: '@yada/app-trips',
  name: 'Trips',
  version: '0.1.0',
  slots: { main: null, leftDrawer: null, header: null, settings: null },
  openDrawerByDefault: true,
  load: () => import('@yada/app-trips').then(m => {
    tripsManifest.slots = m.manifest.slots;
    return m.pluginModule;
  }),
  permissions: {
    crossAppStorage: [],
  },
  metadataExtractor: (data: any) => ({
    documentType: 'trip',
    itemCount: Array.isArray(data?.trips) ? data.trips.length : 0,
  }),
};

export const ALL_MANIFESTS: AppManifest[] = [
  habitsManifest,
  mealPlannerManifest,
  homeManifest,
  docsManifest,
  calendarManifest,
  eventsManifest,
  tripsManifest,
  communityVotesManifest,
  chatManifest,
  trainingManifest,
  contactsManifest,
  financeManifest,
  shoppingListManifest,
  todoManifest,
  groupsManifest,
  contentDistributionManifest,
  devtoolsManifest,
  emojiManifest,
  adminManifest,
];
